import { Grid, Typography } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import { useScrollButtonState } from '@ncr-retail/hooks';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CheckMark } from 'src/common/media/Checkmark.svg';
import Pagination from 'src/pagination/pagination';
import VerticalCenter from 'src/vertical-center/vertical-center';
import BaseButton from '../base-button';
import { styles } from './select-list-style';

export const SelectList: React.FC<Props> = ({
  selectListItems,
  title,
  message,
  subMessage,
  acceptLabel,
  onCancel,
  onAccept,
  closeButtonLabel,
}): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>('');
  const [{ currentPage, lastPage, onPageChanged }, scrollRef] = useScrollButtonState();

  return (
    <Grid container direction='column' justifyContent='space-between' data-testid='select-list'>
      <Grid
        className={classes.header}
        container
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        wrap='nowrap'
      >
        <Grid item container wrap={'nowrap'} className={classes.spacing}>
          <Typography variant={'body1'}>{t(title ?? '') ?? ''}</Typography>
        </Grid>
        <Grid item container justifyContent='flex-end'>
          <HelpIcon classes={{ root: `${classes.icon} ${classes.decision}` }} />
        </Grid>
      </Grid>

      <Grid item className={`${classes.displayBody} fullWidth`}>
        <Grid container direction='column' className='fullHeight'>
          <Grid
            item
            className={classes.messageWrapper}
            container
            direction='column'
            justifyContent='center'
            alignItems='center'
            wrap={'nowrap'}
          >
            <Grid item className={classes.messageItem}>
              <Typography className={classes.message} noWrap={true}>
                {message ? t(message) : ''}
              </Typography>
            </Grid>
            <Grid item className={classes.messageItem}>
              <Typography className={classes.subMessage} noWrap={true}>
                {subMessage ? t(subMessage) : ''}
              </Typography>
            </Grid>
          </Grid>
          <Grid item className={`fullWidth ${classes.eligibilityContainer}`}>
            <div ref={scrollRef} className='verticalScroll fullWidth fullHeight'>
              <Grid container className={classes.resultsContainer}>
                {selectListItems.map(
                  (selectListItem, index): JSX.Element => (
                    <Grid item key={selectListItem.id}>
                      <Grid
                        item
                        data-testid={`select-${index}`}
                        className={`${classes.resultsItem} ${
                          selectListItem.id === selected ? `${classes.selected}` : ''
                        }`}
                        onClick={(): void => setSelected(selectListItem.id)}
                      >
                        <Grid
                          container
                          className={`fullHeight`}
                          justifyContent='space-between'
                          alignContent='center'
                          direction='row'
                        >
                          <Grid className={classes.resultsRowItemContainer}>
                            {selectListItem.id === selected && (
                              <Grid item>
                                <CheckMark className={classes.badge} />
                              </Grid>
                            )}
                            <Grid item>
                              <Typography variant={'body2'}>
                                {t(selectListItem.description || '') || selectListItem.id}
                              </Typography>
                            </Grid>
                          </Grid>
                          {selectListItem.price && (
                            <Grid className={classes.resultsRowItemContainer}>
                              {t(selectListItem.price)}
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  ),
                )}
              </Grid>
            </div>
          </Grid>
          <Grid item className={classes.selectListBottomContainer}>
            <Grid container alignContent='flex-end' className='fullHeight'>
              <Grid item xs={3}>
                <Grid container justifyContent='flex-start'>
                  <Grid item>
                    <VerticalCenter>
                      <Grid container justifyContent='center' alignContent='center'>
                        <BaseButton
                          buttonType='white'
                          data-testid='select-list-close-button'
                          color='default'
                          className={classes.closeButton}
                          onClick={onCancel}
                        >
                          <span className='label'>
                            {closeButtonLabel ?? t('ButtonCancelStyle_Cancel')}
                          </span>
                        </BaseButton>
                      </Grid>
                    </VerticalCenter>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Grid container>
                  {selectListItems.length > 0 && (
                    <Grid container justifyContent='center' className='fullHeight'>
                      <Pagination
                        currentPage={currentPage}
                        totalPages={lastPage}
                        updateCurrentPageHandler={onPageChanged}
                        testIdPrefix={'select-list'}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={3}>
                <Grid container justifyContent='flex-end'>
                  <Grid item>
                    <VerticalCenter>
                      <Grid container justifyContent='center' alignContent='center'>
                        <BaseButton
                          buttonType='primary'
                          data-testid='select-list-accept-button'
                          className={classes.comfirmButton}
                          onClick={() => {
                            onAccept(selected);
                          }}
                          disabled={selected === ''}
                        >
                          <span className='label'>
                            {acceptLabel ? t(acceptLabel) : t('Confirm')}
                          </span>
                        </BaseButton>
                      </Grid>
                    </VerticalCenter>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export interface SelectListItem {
  id: string;
  description?: string;
  price?: string;
}

interface Props {
  selectListItems: SelectListItem[];
  title?: string;
  message?: string;
  subMessage?: string;
  acceptLabel?: string;
  closeButtonLabel?: string;
  onCancel: () => void;
  onAccept: (id: string) => void;
}
